import type {
  ReceivableActivityCategoryColumn,
  ReceivableFeeCategoryColumn,
  ReceivablePeriodDetailRow,
} from '../api/paymentApi';
import { isHandoverFeeCode } from './feeCategoryCodeGrouping';

export function hasNonZeroLedgerAmount(value: number | undefined | null): boolean {
  return Math.round(value ?? 0) !== 0;
}

function hasNonZeroAmount(value: number | undefined | null): boolean {
  return hasNonZeroLedgerAmount(value);
}

/** Hide fee columns with no amounts in the selected ledger buckets (uses filtered totals row). */
export function filterReceivableFeeColumnsWithAmounts(
  columns: ReceivableFeeCategoryColumn[],
  totals: ReceivablePeriodDetailRow | null | undefined,
  scopes: { opening?: boolean; incurred?: boolean; payment?: boolean },
): ReceivableFeeCategoryColumn[] {
  if (!totals) return columns;
  const useOpening = scopes.opening ?? false;
  const useIncurred = scopes.incurred ?? false;
  const usePayment = scopes.payment ?? false;
  if (!useOpening && !useIncurred && !usePayment) return columns;

  // Số dư đầu kỳ grid: Nợ / Có totals only (CNNBG and other fees roll into Nợ).
  if (useOpening && !useIncurred && !usePayment) {
    return [];
  }

  return columns.filter((column) => {
    if (isHandoverFeeCode(column.feeCode)) {
      if (useIncurred || useOpening) {
        return false;
      }
      return (
        usePayment && hasNonZeroAmount(totals.paymentByFee?.[column.id])
      );
    }
    if (useOpening && hasNonZeroAmount(totals.openingByFee?.[column.id])) {
      return true;
    }
    if (useIncurred && hasNonZeroAmount(totals.incurredByFee?.[column.id])) {
      return true;
    }
    if (usePayment && hasNonZeroAmount(totals.paymentByFee?.[column.id])) {
      return true;
    }
    return false;
  });
}

export function filterReceivableFeeColumnsAnyAmount(
  columns: ReceivableFeeCategoryColumn[],
  totals: ReceivablePeriodDetailRow | null | undefined,
): ReceivableFeeCategoryColumn[] {
  return filterReceivableFeeColumnsWithAmounts(columns, totals, {
    opening: true,
    incurred: true,
    payment: true,
  });
}

export function filterReceivableActivityColumnsWithAmounts(
  columns: ReceivableActivityCategoryColumn[],
  totals: ReceivablePeriodDetailRow | null | undefined,
  field: 'collectionActivities' | 'spendingActivities',
): ReceivableActivityCategoryColumn[] {
  if (!totals) return columns;
  return columns.filter((column) =>
    hasNonZeroAmount(totals[field]?.[column.id]),
  );
}
